/**
 * Stats panel component - displays CPU, memory, network, disk, and token usage metrics.
 *
 * @module components/terminal/StatsPanel
 */

import {
  Cpu,
  MemoryStick,
  HardDrive,
  Wifi,
  Loader2,
  AlertCircle,
  RefreshCw,
  Activity,
  Clock,
  MessageSquare,
} from 'lucide-react';
import type { StatsData, CpuStats } from './useSandboxMonitor';
import type { ConnectionState } from '@/hooks';
import type { TokenUsageStats } from '@/types';

interface StatsPanelProps {
  connectionState: ConnectionState;
  stats: StatsData | null;
  prevCpu: CpuStats | null;
  networkRate: { rx: number; tx: number };
  error: string | null;
  onRetry: () => void;
  tokenUsage?: TokenUsageStats;
  compact?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatKB(kb: number): string {
  return formatBytes(kb * 1024);
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function formatTokenCount(count: number): string {
  if (count >= 1_000_000) {
    return (count / 1_000_000).toFixed(1) + 'M';
  }
  if (count >= 1_000) {
    return (count / 1_000).toFixed(1) + 'K';
  }
  return count.toLocaleString();
}

function calculateCpuUsage(cpu: CpuStats, prevCpu: CpuStats | null): number {
  if (!prevCpu) return 0;
  const prevTotal =
    prevCpu.user +
    prevCpu.nice +
    prevCpu.system +
    prevCpu.idle +
    prevCpu.iowait +
    prevCpu.irq +
    prevCpu.softirq +
    prevCpu.steal;
  const currTotal =
    cpu.user + cpu.nice + cpu.system + cpu.idle + cpu.iowait + cpu.irq + cpu.softirq + cpu.steal;
  const totalDiff = currTotal - prevTotal;
  const idleDiff = cpu.idle - prevCpu.idle;
  if (totalDiff === 0) return 0;
  return Math.round(((totalDiff - idleDiff) / totalDiff) * 100);
}

/** Gauge card for compact grid – fixed-size gauge, no flex expanding */
function CompactGridGaugeCard({
  label,
  percent,
  detail,
  icon: Icon,
  iconBg,
  iconColor,
  colorClass,
  badge,
}: {
  label: string;
  percent: number;
  detail?: string;
  icon: typeof Cpu;
  iconBg: string;
  iconColor: string;
  colorClass: string;
  badge?: string;
}) {
  return (
    <div className="bg-white dark:bg-[#161616] rounded-lg p-2 border border-gray-200 dark:border-[#2a2a2a] overflow-hidden">
      <div className="flex items-center gap-1.5">
        <div className={`p-1 rounded-md ${iconBg}`}>
          <Icon className={`w-3 h-3 ${iconColor}`} />
        </div>
        <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">{label}</span>
        {badge && (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-gray-400">
            <Clock className="w-2.5 h-2.5" />
            {badge}
          </span>
        )}
      </div>
      <div className="flex items-center justify-center py-1.5">
        <svg className="w-12 h-12 -rotate-90" viewBox="0 0 40 40">
          <circle
            cx="20"
            cy="20"
            r="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="5"
            className="text-gray-200 dark:text-[#2a2a2a]"
          />
          <circle
            cx="20"
            cy="20"
            r="16"
            fill="none"
            strokeWidth="5"
            strokeLinecap="round"
            className={colorClass}
            strokeDasharray={`${percent * 1.005} 100.5`}
            style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.22, 1, 0.36, 1)' }}
          />
          <text
            x="20"
            y="20"
            textAnchor="middle"
            dominantBaseline="central"
            className="fill-gray-900 dark:fill-white"
            style={{ fontSize: '10px', fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}
            transform="rotate(90, 20, 20)"
          >
            {percent}%
          </text>
        </svg>
      </div>
      {detail && (
        <div className="text-[10px] text-center text-gray-500 dark:text-gray-400 truncate">
          {detail}
        </div>
      )}
    </div>
  );
}

export function StatsPanel({
  connectionState,
  stats,
  prevCpu,
  networkRate,
  error,
  onRetry,
  tokenUsage,
  compact,
}: StatsPanelProps) {
  const cpuUsage = stats ? calculateCpuUsage(stats.cpu, prevCpu) : 0;
  const memoryUsed = stats ? stats.memory.total_kb - stats.memory.available_kb : 0;
  const memoryPercent = stats ? Math.round((memoryUsed / stats.memory.total_kb) * 100) : 0;
  const diskPercent = stats?.disk[0] ? parseInt(stats.disk[0].percent) || 0 : 0;

  // ─── COMPACT MODE: grid-based stats for alternative layout ───
  if (compact) {
    const isLoading =
      connectionState === 'disconnected' ||
      connectionState === 'connecting' ||
      (connectionState === 'connected' && !stats);
    const hasError = error || connectionState === 'error';

    if (isLoading) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-xs">Loading stats...</span>
          </div>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-xs text-red-500">{error || 'Connection failed'}</span>
            <button onClick={onRetry} className="ml-2 text-xs text-blue-500 hover:underline">
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full overflow-y-auto">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 p-1">
          {/* CPU */}
          <CompactGridGaugeCard
            label="CPU"
            percent={cpuUsage}
            detail={`Load: ${stats?.loadavg['1min'].toFixed(2)} / ${stats?.loadavg['5min'].toFixed(2)}`}
            icon={Cpu}
            iconBg="bg-blue-100 dark:bg-blue-900/50"
            iconColor="text-blue-600 dark:text-blue-400"
            colorClass={
              cpuUsage > 80
                ? 'text-red-500 stroke-current'
                : cpuUsage > 50
                  ? 'text-yellow-500 stroke-current'
                  : 'text-blue-500 stroke-current'
            }
            badge={
              stats?.uptime_secs !== undefined ? `Up ${formatUptime(stats.uptime_secs)}` : undefined
            }
          />
          {/* Memory */}
          <CompactGridGaugeCard
            label="Memory"
            percent={memoryPercent}
            detail={`${formatKB(memoryUsed)} / ${formatKB(stats?.memory.total_kb || 0)}`}
            icon={MemoryStick}
            iconBg="bg-green-100 dark:bg-green-900/50"
            iconColor="text-green-600 dark:text-green-400"
            colorClass={
              memoryPercent > 90
                ? 'text-red-500 stroke-current'
                : memoryPercent > 70
                  ? 'text-yellow-500 stroke-current'
                  : 'text-green-500 stroke-current'
            }
          />
          {/* Disk */}
          <CompactGridGaugeCard
            label="Storage"
            percent={diskPercent}
            detail={stats?.disk[0] ? `${stats.disk[0].used} / ${stats.disk[0].size}` : '—'}
            icon={HardDrive}
            iconBg="bg-orange-100 dark:bg-orange-900/50"
            iconColor="text-orange-600 dark:text-orange-400"
            colorClass={
              diskPercent > 90
                ? 'text-red-500 stroke-current'
                : diskPercent > 70
                  ? 'text-yellow-500 stroke-current'
                  : 'text-orange-500 stroke-current'
            }
          />
          {/* Network */}
          <div className="bg-white dark:bg-[#161616] rounded-lg p-2 border border-gray-200 dark:border-[#2a2a2a] overflow-hidden">
            <div className="flex items-center gap-1.5">
              <div className="p-1 bg-purple-100 dark:bg-purple-900/50 rounded-md">
                <Wifi className="w-3 h-3 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
                Network
              </span>
            </div>
            <div className="mt-1.5 space-y-1 text-[11px]">
              <div className="flex justify-between items-center">
                <span className="text-green-500">
                  ↓ <span className="text-gray-500 dark:text-gray-400">Down</span>
                </span>
                <span className="font-mono font-medium text-green-700 dark:text-green-400">
                  {formatBytes(networkRate.rx)}/s
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-blue-500">
                  ↑ <span className="text-gray-500 dark:text-gray-400">Up</span>
                </span>
                <span className="font-mono font-medium text-blue-700 dark:text-blue-400">
                  {formatBytes(networkRate.tx)}/s
                </span>
              </div>
              <div className="pt-1 border-t border-gray-100 dark:border-[#2a2a2a]">
                <div className="flex justify-between items-center text-[10px]">
                  <span className="text-gray-500 dark:text-gray-400">Total Download</span>
                  <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
                    {formatBytes(stats?.network.rx_bytes || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-0.5 text-[10px]">
                  <span className="text-gray-500 dark:text-gray-400">Total Upload</span>
                  <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
                    {formatBytes(stats?.network.tx_bytes || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Tokens */}
          {tokenUsage && (
            <div className="bg-white dark:bg-[#161616] rounded-lg p-2 border border-gray-200 dark:border-[#2a2a2a] overflow-hidden">
              <div className="flex items-center gap-1.5">
                <div className="p-1 bg-indigo-100 dark:bg-indigo-900/50 rounded-md">
                  <MessageSquare className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                </div>
                <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
                  Tokens
                </span>
              </div>
              <div className="mt-1.5 space-y-1 text-[11px]">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 dark:text-gray-400">Input</span>
                  <span className="font-mono font-medium text-indigo-600 dark:text-indigo-400">
                    {formatTokenCount(tokenUsage.totalInputTokens)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 dark:text-gray-400">Output</span>
                  <span className="font-mono font-medium text-indigo-600 dark:text-indigo-400">
                    {formatTokenCount(tokenUsage.totalOutputTokens)}
                  </span>
                </div>
                <div className="pt-1 border-t border-gray-100 dark:border-[#2a2a2a]">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400">Total</span>
                    <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
                      {formatTokenCount(tokenUsage.totalInputTokens + tokenUsage.totalOutputTokens)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-0.5">
                    <span className="text-gray-500 dark:text-gray-400">Requests</span>
                    <span className="font-mono text-gray-500 dark:text-gray-400">
                      {tokenUsage.requestCount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── FULL MODE (classic layout) ───
  if (
    connectionState === 'disconnected' ||
    connectionState === 'connecting' ||
    (connectionState === 'connected' && !stats)
  ) {
    return (
      <div className="flex flex-col bg-white dark:bg-[#161616] rounded-lg border border-gray-200 dark:border-[#2a2a2a] overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-[#2a2a2a]">
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-medium text-gray-900 dark:text-gray-200">Stats</span>
          </div>
        </div>
        <div className="flex items-center justify-center py-6">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading stats...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || connectionState === 'error') {
    return (
      <div className="flex flex-col bg-white dark:bg-[#161616] rounded-lg border border-gray-200 dark:border-[#2a2a2a] overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-[#2a2a2a]">
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-red-500" />
            <span className="text-xs font-medium text-gray-900 dark:text-gray-200">Stats</span>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-2 py-6">
          <div className="flex items-center gap-2 text-red-500">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error || 'Connection failed'}</span>
          </div>
          <button
            onClick={onRetry}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-500 text-white rounded-sm hover:bg-blue-600"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white dark:bg-[#161616] rounded-lg border border-gray-200 dark:border-[#2a2a2a] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-[#2a2a2a]">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-green-500" />
          <span className="text-xs font-medium text-gray-900 dark:text-gray-200">Stats</span>
          <span className="text-[10px] text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900 px-1.5 py-0.5 rounded-sm">
            Live
          </span>
        </div>
        <div className="flex items-center gap-3">
          {stats?.uptime_secs !== undefined && (
            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <Clock className="w-3 h-3" />
              <span>Up {formatUptime(stats.uptime_secs)}</span>
            </div>
          )}
          <button
            onClick={onRetry}
            className="p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-sm"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Stats Grid — horizontal layout */}
      <div className="p-2.5 bg-linear-to-br from-gray-50 to-gray-100 dark:from-[#0a0a0a] dark:to-[#161616]">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
          {/* CPU */}
          <div className="bg-white dark:bg-[#161616] rounded-lg p-2.5 shadow-xs border border-gray-200 dark:border-[#2a2a2a] flex flex-col">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="p-1 bg-blue-100 dark:bg-blue-900 rounded-md">
                <Cpu className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">CPU</span>
            </div>
            <div className="flex items-center justify-center py-1">
              <div className="relative w-14 h-14">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 40 40">
                  <circle
                    cx="20"
                    cy="20"
                    r="16"
                    stroke="currentColor"
                    strokeWidth="5"
                    fill="none"
                    className="text-gray-200 dark:text-[#2a2a2a]"
                  />
                  <circle
                    cx="20"
                    cy="20"
                    r="16"
                    stroke="currentColor"
                    strokeWidth="5"
                    fill="none"
                    strokeLinecap="round"
                    className={
                      cpuUsage > 80
                        ? 'text-red-500'
                        : cpuUsage > 50
                          ? 'text-yellow-500'
                          : 'text-blue-500'
                    }
                    strokeDasharray={`${cpuUsage * 1.005} 100.5`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {cpuUsage}%
                  </span>
                </div>
              </div>
            </div>
            <div className="text-[10px] text-center text-gray-500 dark:text-gray-400">
              Load: {stats?.loadavg['1min'].toFixed(2)} / {stats?.loadavg['5min'].toFixed(2)}
            </div>
          </div>

          {/* Memory */}
          <div className="bg-white dark:bg-[#161616] rounded-lg p-2.5 shadow-xs border border-gray-200 dark:border-[#2a2a2a] flex flex-col">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="p-1 bg-green-100 dark:bg-green-900 rounded-md">
                <MemoryStick className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Memory</span>
            </div>
            <div className="flex items-center justify-center py-1">
              <div className="relative w-14 h-14">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 40 40">
                  <circle
                    cx="20"
                    cy="20"
                    r="16"
                    stroke="currentColor"
                    strokeWidth="5"
                    fill="none"
                    className="text-gray-200 dark:text-[#2a2a2a]"
                  />
                  <circle
                    cx="20"
                    cy="20"
                    r="16"
                    stroke="currentColor"
                    strokeWidth="5"
                    fill="none"
                    strokeLinecap="round"
                    className={
                      memoryPercent > 90
                        ? 'text-red-500'
                        : memoryPercent > 70
                          ? 'text-yellow-500'
                          : 'text-green-500'
                    }
                    strokeDasharray={`${memoryPercent * 1.005} 100.5`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {memoryPercent}%
                  </span>
                </div>
              </div>
            </div>
            <div className="text-[10px] text-center text-gray-500 dark:text-gray-400">
              {formatKB(memoryUsed)} / {formatKB(stats?.memory.total_kb || 0)}
            </div>
          </div>

          {/* Network */}
          <div className="bg-white dark:bg-[#161616] rounded-lg p-2.5 shadow-xs border border-gray-200 dark:border-[#2a2a2a]">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="p-1 bg-purple-100 dark:bg-purple-900 rounded-md">
                <Wifi className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Network</span>
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-green-500">↓</span>
                  <span className="text-gray-500 dark:text-gray-400">Download</span>
                </div>
                <span className="font-mono font-medium text-green-700 dark:text-green-400">
                  {formatBytes(networkRate.rx)}/s
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-blue-500">↑</span>
                  <span className="text-gray-500 dark:text-gray-400">Upload</span>
                </div>
                <span className="font-mono font-medium text-blue-700 dark:text-blue-400">
                  {formatBytes(networkRate.tx)}/s
                </span>
              </div>
              <div className="pt-1.5 border-t border-gray-100 dark:border-[#2a2a2a]">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 dark:text-gray-400">Total Download</span>
                  <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
                    {formatBytes(stats?.network.rx_bytes || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-0.5">
                  <span className="text-gray-500 dark:text-gray-400">Total Upload</span>
                  <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
                    {formatBytes(stats?.network.tx_bytes || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Disk */}
          <div className="bg-white dark:bg-[#161616] rounded-lg p-2.5 shadow-xs border border-gray-200 dark:border-[#2a2a2a]">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="p-1 bg-orange-100 dark:bg-orange-900 rounded-md">
                <HardDrive className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
              </div>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Storage</span>
            </div>
            <div className="space-y-1.5">
              {stats?.disk.slice(0, 3).map((d, i) => (
                <div key={i} className="text-[11px]">
                  <div className="flex justify-between mb-0.5">
                    <span className="text-gray-600 dark:text-gray-400" title={d.mount ?? d.label}>
                      {d.label || d.mount || 'Unknown'}
                    </span>
                    <span className="font-mono text-gray-600 dark:text-gray-400">
                      {d.used} / {d.size}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-[#2a2a2a] rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all ${
                        parseInt(d.percent) > 90
                          ? 'bg-red-500'
                          : parseInt(d.percent) > 70
                            ? 'bg-yellow-500'
                            : 'bg-orange-500'
                      }`}
                      style={{ width: `${Math.min(100, parseInt(d.percent) || 0)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Token Usage - only shown if LLM interception is enabled */}
          {tokenUsage && (
            <div className="bg-white dark:bg-[#161616] rounded-lg p-2.5 shadow-xs border border-gray-200 dark:border-[#2a2a2a]">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="p-1 bg-indigo-100 dark:bg-indigo-900 rounded-md">
                  <MessageSquare className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Tokens</span>
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 dark:text-gray-400">Input</span>
                  <span className="font-mono font-medium text-indigo-600 dark:text-indigo-400">
                    {formatTokenCount(tokenUsage.totalInputTokens)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 dark:text-gray-400">Output</span>
                  <span className="font-mono font-medium text-indigo-600 dark:text-indigo-400">
                    {formatTokenCount(tokenUsage.totalOutputTokens)}
                  </span>
                </div>
                <div className="pt-1.5 border-t border-gray-100 dark:border-[#2a2a2a]">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400">Total</span>
                    <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
                      {formatTokenCount(tokenUsage.totalInputTokens + tokenUsage.totalOutputTokens)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-0.5">
                    <span className="text-gray-500 dark:text-gray-400">Requests</span>
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      {tokenUsage.requestCount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
